import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AlarmClock, FileText, User, Wallet, PhoneCall, AlertTriangle, CheckCircle2 } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { inr, fdate, fdt } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { hasPerm } from "@/lib/perms";
import { CollectModal } from "@/pages/Invoices";

const STATUS_LABEL = { unpaid: "Unpaid", partially_paid: "Partially paid" };
const btn = "inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50";

function Row({ r, perms, onCollect, onContact, busy }) {
  return (
    <div className="flex flex-col gap-3 py-3 sm:flex-row sm:items-center sm:justify-between" data-testid={`urgent-row-${r.id}`}>
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-semibold text-pawkt-text" data-testid={`urgent-customer-${r.id}`}>{r.customer_name || "Unknown customer"}</span>
          <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${r.status === "unpaid" ? "bg-rose-50 text-rose-600" : "bg-amber-50 text-amber-700"}`} data-testid={`urgent-status-${r.id}`}>{STATUS_LABEL[r.status] || r.status}</span>
        </div>
        <div className="mt-0.5 text-xs text-pawkt-text-secondary">
          {r.invoice_number} · {fdate(r.created_at)} · Total {inr(r.total)} · Collected <span data-testid={`urgent-paid-${r.id}`}>{inr(r.amount_paid)}</span>
        </div>
        {r.last_contacted_at && (
          <div className="mt-0.5 text-[11px] text-pawkt-text-disabled" data-testid={`urgent-contacted-${r.id}`}>
            Contacted {fdt(r.last_contacted_at)}{r.last_contacted_by_name ? ` by ${r.last_contacted_by_name}` : ""} · still unpaid
          </div>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2 sm:justify-end">
        <span className="mr-1 text-sm font-extrabold text-pawkt-text" data-testid={`urgent-balance-${r.id}`}>{inr(r.balance)}</span>
        <Link to={`/invoices?search=${encodeURIComponent(r.invoice_number || "")}`} className={`${btn} bg-pawkt-elevated text-pawkt-text-secondary hover:bg-slate-200`} data-testid={`urgent-open-invoice-${r.id}`}><FileText className="h-3 w-3" /> Invoice</Link>
        {perms.customers && r.customer_id && <Link to={`/customers/${r.customer_id}`} className={`${btn} bg-pawkt-elevated text-pawkt-text-secondary hover:bg-slate-200`} data-testid={`urgent-open-customer-${r.id}`}><User className="h-3 w-3" /> Customer</Link>}
        {perms.contact && r.customer_id && <button type="button" disabled={busy} onClick={() => onContact(r)} className={`${btn} bg-pawkt-elevated text-pawkt-text-secondary hover:bg-slate-200 disabled:opacity-50`} data-testid={`urgent-mark-contacted-${r.id}`}><PhoneCall className="h-3 w-3" /> Mark contacted</button>}
        {perms.collect && <button type="button" onClick={() => onCollect(r)} className={`${btn} bg-pawkt-primary text-white hover:bg-pawkt-primary-hover`} data-testid={`urgent-record-payment-${r.id}`}><Wallet className="h-3 w-3" /> Record payment</button>}
      </div>
    </div>
  );
}

export function UrgentCollections() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [pay, setPay] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const perms = { collect: hasPerm(user, "payments.create"), contact: hasPerm(user, "communications.send"), customers: hasPerm(user, "customers.view") };
  const { data, isLoading } = useQuery({
    queryKey: ["urgent-collections"],
    queryFn: async () => (await api.get("/dashboard/urgent-collections")).data,
    refetchOnWindowFocus: false,
  });
  const refresh = () => ["urgent-collections", "outstanding-customers", "invoices", "dashboard"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  const contact = async (r) => {
    setBusyId(r.id);
    try { await api.post(`/dashboard/urgent-collections/${r.id}/contacted`); toast.success("Marked as contacted — payment still pending"); qc.invalidateQueries({ queryKey: ["urgent-collections"] }); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusyId(null); }
  };

  // Hidden entirely until the server says the urgency date has been reached (business timezone).
  if (isLoading || !data?.active) return null;

  return (
    <div className="lg:col-span-3 rounded-3xl border border-pawkt-border bg-white p-5 sm:p-6 shadow-card" data-testid="urgent-collections">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-2 font-display text-lg font-extrabold text-pawkt-text">
            <AlarmClock className="h-5 w-5 text-rose-500" /> Urgent Collections
            <span className="rounded-full bg-rose-50 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-rose-600" data-testid="urgent-collections-active-badge">Urgent</span>
          </h3>
          <p className="text-xs text-pawkt-text-secondary">{`${data.month_label} invoices · urgency from day ${data.effective_day}`}</p>
        </div>
        <div className="text-right">
            <div className="text-xl font-extrabold text-pawkt-text" data-testid="urgent-collections-total">{inr(data.total_outstanding)}</div>
            <div className="text-xs text-pawkt-text-secondary" data-testid="urgent-collections-count">{data.invoice_count} invoice{data.invoice_count === 1 ? "" : "s"} to collect</div>
        </div>
      </div>

      {(
          <>
            {data.needs_review?.count > 0 && (
              <div className="mt-4 flex items-center gap-2 rounded-2xl bg-amber-50 px-4 py-2.5 text-xs text-amber-800" data-testid="urgent-collections-needs-review">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                {data.needs_review.count} invoice{data.needs_review.count === 1 ? "" : "s"} ({inr(data.needs_review.amount)}) need review — refunds or balance mismatch. Not counted above.
              </div>
            )}
            {!data.items.length ? (
              <div className="mt-6 flex items-center gap-2 text-sm text-pawkt-text-secondary" data-testid="urgent-collections-empty">
                <CheckCircle2 className="h-5 w-5 text-emerald-500" /> Nothing outstanding from {data.month_label}. Great collecting!
              </div>
            ) : (
              <div className="mt-3 divide-y divide-slate-100" data-testid="urgent-collections-list">
                {data.items.map((r) => <Row key={r.id} r={r} perms={perms} busy={busyId === r.id} onCollect={setPay} onContact={contact} />)}
                {data.capped && <p className="pt-3 text-xs text-pawkt-text-secondary" data-testid="urgent-collections-capped">Showing top {data.items.length} of {data.invoice_count}. Totals include all.</p>}
              </div>
            )}
          </>
        )}
      {pay && <CollectModal invoice={pay} canBackdate={hasPerm(user, "payments.backdate")} onClose={() => setPay(null)} onDone={() => { setPay(null); refresh(); }} />}
    </div>
  );
}
